import { DeleteOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Flex, Form, Input, Row, Select, Typography } from "antd";
import { useGetConnectionSequenceStepsQuery } from "api/TenantSlug/ConnectionSequenceStep/hooks";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { StructureAutocompleteScenarioSelectSearchable } from "components/UI/Selects/StructureAutocompleteScenarioSelectSearchable/StructureAutocompleteScenarioSelectSearchable.tsx";
import { StructureModifyScenarioSelectSearchable } from "components/UI/Selects/StructureModifyScenarioSelectSearchable/StructureModifyScenarioSelectSearchable.tsx";
import { StructureValidateScenarioSelectSearchable } from "components/UI/Selects/StructureValidateScenarioSelectSearchable/StructureValidateScenarioSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { FormFieldTypes } from "types/enums";
import { handleShowSearchFilterOption } from "utils/handleShowSearchFilterOption";

import { RegexTester } from "./RegexTester";
import { FormFieldsFormData } from "../FormFieldsForm/formSchema";

import ConnectionSequenceStepData = App.Data.ConnectionSequenceStepData;

interface FormFieldSettingsFormContentProps {
    type: FormFieldTypes;
    formFieldId: string;
    formStructureId: string;
}

export const FormFieldSettingsFormContent: FC<FormFieldSettingsFormContentProps> = ({
    type,
    formFieldId,
    formStructureId,
}) => {
    const { t: tF } = usePaldockTranslation("forms.form-field-settings");
    const { t: tC } = usePaldockTranslation("common");

    const generalFormUtils = useGeneralForm();
    const { control, getValues, setValue } = generalFormUtils;

    const queryString = `meanings,
    requestConfig.responseHandlers.responseConnectionPropertiesCheck,
    requestConfig.requestConnectionPropertiesUrl,
    requestConfig.requestConnectionPropertiesUrl.requestConnectionPropertyModify,
    requestConfig.requestConnectionProperties,
    requestConfig.requestConnectionProperties.requestConnectionPropertyModify,
    requestConfig.headers,
    requestConfig.headers.requestConnectionPropertyModify,
    requestConfig.responseHandlers.stepConnection,
    requestConfig.responseHandlers.responseConnectionProperties,
    requestConfig.responseHandlers.responseConnectionProperties.requestConnectionPropertyModify`;

    const getConnectionSequenceStepsQuery = useGetConnectionSequenceStepsQuery({
        filter: {
            include: queryString.replace(/\s+/g, ""),
            filter: { form_structure_id: formStructureId },
        },
    });

    const connectionSteps = getConnectionSequenceStepsQuery.data?.connectionSequenceSteps;

    const connectionStepsOptions =
        connectionSteps?.map((step) => ({
            label: step.name,
            value: step.id || "",
        })) || [];

    const formFields = getValues("formFields");
    const index = formFields.findIndex((field: FormFieldsFormData) => field.id === formFieldId);
    const propertyPath = `formFields.${index}` as const;

    const frontendFormat = useWatch({ control, name: `${propertyPath}.frontend_format` });
    const validationRegex = useWatch({ control, name: `${propertyPath}.validation_regex` });

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${propertyPath}.formFieldSelectItems`,
    });

    const extractSystemNames = (step: ConnectionSequenceStepData): string => {
        const systemNames: string[] = [];

        step.requestConfig?.responseHandlers?.forEach((handler) => {
            if (!handler) {
                return;
            }
            handler.responseConnectionProperties?.forEach((property) => {
                if (!property) {
                    return;
                }
                systemNames.push(property.system_name);
            });
        });
        return systemNames.join(",");
    };

    const handleSetAccessor = (accessorField: string, stepData: ConnectionSequenceStepData | null) => {
        if (!stepData) {
            setValue(accessorField, null);
            return;
        }

        const accessorValue = extractSystemNames(stepData);

        if (accessorValue) {
            setValue(accessorField, accessorValue);
        } else {
            setValue(accessorField, null);
        }
    };

    return (
        <div className={"bg-gray-100 rounded-lg border-dashed border"}>
            <Form layout="vertical">
                <div className="gap-x-4  p-4 bg-white m-3 rounded-lg">
                    <div className="grid grid-cols-2 gap-x-4 col-span-2">
                        <Typography.Title level={4} className="col-span-2 my-3">
                            {tC("settings")}
                        </Typography.Title>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.visible_description`}
                            label={tF("fields.visible_description.label")}
                        >
                            <Input />
                        </FormItem>

                        <FormItem
                            control={control}
                            name={`${propertyPath}.default_value`}
                            label={tF("fields.default_value.label")}
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.example_value`}
                            label={tF("fields.example_value.label")}
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.not_show_if_system_name_equals`}
                            label={tF("fields.not_show_if_system_name_equals.label")}
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.show_only_if_system_name_equals`}
                            label={tF("fields.show_only_if_system_name_equals.label")}
                        >
                            <Input />
                        </FormItem>
                    </div>

                    <div className="grid grid-cols-1 gap-x-4 col-span-1">
                        <FormItem
                            control={control}
                            name={`${propertyPath}.doc_note`}
                            label={tF("fields.doc_note.label")}
                        >
                            <Input />
                        </FormItem>
                        <Row className={"flex gap-x-3"}>
                            <FormItem control={control} name={`${propertyPath}.is_required`} valuePropName="checked">
                                <Checkbox>{tF("fields.is_required.label")}</Checkbox>
                            </FormItem>
                            <FormItem control={control} name={`${propertyPath}.is_hidden`} valuePropName="checked">
                                <Checkbox>{tF("fields.is_hidden.label")}</Checkbox>
                            </FormItem>
                        </Row>
                    </div>
                </div>
            </Form>

            <Form layout="vertical">
                <div className="gap-x-4 grid-cols-3 p-4 col-span-3 bg-white m-3 rounded-lg">
                    <div className="col-span-3 my-3">
                        <Flex justify={"space-between"}>
                            <Typography.Title level={4}>{tF("sections.validation.title")}</Typography.Title>
                        </Flex>
                    </div>

                    <div className="grid grid-cols-1 gap-x-4 col-span-1">
                        <FormItem
                            control={control}
                            name={`${propertyPath}.frontend_format`}
                            label={tF("fields.frontend_format.label")}
                            className="!mb-2"
                        >
                            <Input.TextArea />
                        </FormItem>
                        <p className="mb-2 text-xs text-gray-500">{tF("fields.frontend_format.help")}</p>
                        <RegexTester pattern={frontendFormat} />
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 col-span-1">
                        <FormItem
                            control={control}
                            name={`${propertyPath}.validation_regex`}
                            label={tF("fields.validation_regex.label")}
                            className="!mb-2"
                        >
                            <Input.TextArea />
                        </FormItem>
                        <p className="mb-2 text-xs text-gray-500">{tF("fields.validation_regex.help")}</p>
                        <RegexTester pattern={validationRegex} isBackend />
                    </div>

                    <div className="grid grid-cols-3 gap-x-4 col-span-3">
                        <FormItem
                            control={control}
                            name={`${propertyPath}.validation_sequence_step_id`}
                            label={tF("fields.validation_sequence_step_id_old.label")}
                        >
                            <Select
                                showSearch
                                placeholder={tF("fields.validation_sequence_step_id_old.placeholder")}
                                options={connectionStepsOptions}
                                allowClear={true}
                                filterOption={handleShowSearchFilterOption}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.data_modification_sequence_step_id`}
                            label={tF("fields.data_modification_sequence_step_id_old.label")}
                        >
                            <Select
                                showSearch
                                placeholder={tF("fields.data_modification_sequence_step_id_old.placeholder")}
                                options={connectionStepsOptions}
                                allowClear={true}
                                filterOption={handleShowSearchFilterOption}
                                onChange={(value) => {
                                    const stepData = connectionSteps?.find((step) => step.id === value) || null;
                                    handleSetAccessor(`${propertyPath}.data_modification_accessor`, stepData);
                                }}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.autocomplete_sequence_step_id`}
                            label={tF("fields.autocomplete_sequence_step_id_old.label")}
                        >
                            <Select
                                showSearch
                                placeholder={tF("fields.autocomplete_sequence_step_id_old.placeholder")}
                                options={connectionStepsOptions}
                                allowClear={true}
                                filterOption={handleShowSearchFilterOption}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.validation_scenarios`}
                            label={tF("fields.validation_scenarios_ids.label")}
                        >
                            <StructureValidateScenarioSelectSearchable
                                formStructureId={formStructureId}
                                placeholder={tF("fields.validation_scenarios_ids.placeholder")}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.modification_scenarios`}
                            label={tF("fields.modification_scenarios_ids.label")}
                        >
                            <StructureModifyScenarioSelectSearchable
                                formStructureId={formStructureId}
                                placeholder={tF("fields.modification_scenarios_ids.placeholder")}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.autocomplete_scenarios`}
                            label={tF("fields.autocomplete_scenarios_ids.label")}
                        >
                            <StructureAutocompleteScenarioSelectSearchable
                                formStructureId={formStructureId}
                                placeholder={tF("fields.autocomplete_scenarios_ids.placeholder")}
                            />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.data_modification_accessor`}
                            label={tF("fields.data_modification_accessor.label")}
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${propertyPath}.autocomplete_accessor`}
                            label={tF("fields.autocomplete_accessor.label")}
                        >
                            <Input />
                        </FormItem>
                    </div>
                </div>
            </Form>
            {type === FormFieldTypes.Select && (
                <SectionContentWrapper withBackground className="mb-2">
                    <Typography.Title level={5}>{tF("sections.select_options.title")}</Typography.Title>
                    <Col span={24}>
                        {fields.map((field, index) => (
                            <Row key={field.id} gutter={16} style={{ marginBottom: 16 }}>
                                <Col span={10}>
                                    <FormItem
                                        control={control}
                                        name={`${propertyPath}.formFieldSelectItems.${index}.value`}
                                        label={tF("fields.select_item_value.label")}
                                        required
                                    >
                                        <Input placeholder={tF("fields.select_item_value.placeholder")} />
                                    </FormItem>
                                </Col>
                                <Col span={11}>
                                    <FormItem
                                        control={control}
                                        name={`${propertyPath}.formFieldSelectItems.${index}.visible_description`}
                                        label={tF("fields.select_item_description.label")}
                                    >
                                        <Input placeholder={tF("fields.select_item_description.placeholder")} />
                                    </FormItem>
                                </Col>
                                <Col span={3} className="flex items-center">
                                    <Button icon={<DeleteOutlined />} onClick={() => remove(index)} />
                                </Col>
                            </Row>
                        ))}
                        <Button
                            type="primary"
                            onClick={() =>
                                append({
                                    form_field_id: null,
                                    id: null,
                                    value: "",
                                    visible_description: "",
                                })
                            }
                        >
                            {tF("actions.add_option")}
                        </Button>
                    </Col>
                </SectionContentWrapper>
            )}
        </div>
    );
};

interface FormFieldSettingsFormProps {
    type: FormFieldTypes;
    formFieldId: string;
    formStructureId: string;
}

export const FormFieldSettingsForm: FC<FormFieldSettingsFormProps> = ({ type, formFieldId, formStructureId }) => {
    return <FormFieldSettingsFormContent type={type} formFieldId={formFieldId} formStructureId={formStructureId} />;
};
